import React from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Ruler, Users, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from 'stores/auth/authStore';
import { useScrollStore } from 'stores/scroll/useScrollStore';

const FeatureItem = ({ Icon, text }) => {
  return (
    <div
      className="flex items-center gap-x-[17px] h-[69px] lg:h-[79px] w-full md:w-[243px] lg:w-[356px] px-5 py-[15.5px] rounded-lg border bg-white"
      style={{
        backgroundColor: 'var(--color-card-bg)',
        borderColor: 'var(--color-card-border)',
        boxShadow: '0px 0px 55.599998474121094px rgba(0, 0, 0, 0.04)'
      }}
    >
      <div className="flex-shrink-0">
        <Icon
          className="w-[42px] h-[44px] text-[var(--color-charcoal)]"
          strokeWidth={1.15}
          aria-hidden
        />
      </div>
      <span
        className='font-montserrat font-semibold !text-[14px] lg:!text-[18px] tracking-[-0.03em] leading-[153%] text-[var(--color-charcoal)]'>
        {text}
      </span>
    </div>
  );
};

export default function CTASection() {
  const navigate = useNavigate();
  const location = useLocation();
  const setToggle = useAuthStore(s => s.setToggle);
  const scrollToSecondSection = useScrollStore(s => s.scrollToSecondSection);

  const goToSignup = () => {
    navigate('/signup', { state: { backgroundLocation: location } });
  };

  const goLearnMore = () => {
    if (location.pathname === '/') {
      setToggle(false);
      scrollToSecondSection();
      return;
    }
    setToggle(false);
    const afterNav = () => {
      window.setTimeout(() => scrollToSecondSection(), 100);
    };
    const doNav = () => {
      flushSync(() => navigate('/'));
    };
    if (typeof document !== 'undefined' && document.startViewTransition) {
      const vt = document.startViewTransition(doNav);
      (vt.finished ?? Promise.resolve()).finally(afterNav);
    } else {
      doNav();
      afterNav();
    }
  };

  const features = [
    { Icon: Ruler, text: 'Scan-driven fit' },
    { Icon: Users, text: 'Your saddle maker' },
    { Icon: ShieldCheck, text: 'Pro-grade foundation' }
  ];

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="container mx-auto px-6 sm:px-0 lg:px-6">
        <div className="flex flex-col lg:flex-row lg:gap-x-[105px]">
          {/* Left Content */}
          <div className="flex flex-col items-stretch sm:items-start text-left w-full">
            <h2 className="uppercase mb-4 md:mb-6 font-barlowCondensed text-[36px] sm:text-[62px] xl:text-[72px] font-bold tracking-[-0.03em] leading-[110%] text-[var(--color-primary)]" >
              Ready when you are
            </h2>
            <p className='font-montserrat font-medium text-[14px] sm:text-[16px] lg:max-w-[475px] tracking-[-0.03em] leading-[153%] text-[var(--color-text-muted)]' >
              When fractions of a second matter, fit cannot be guesswork.
              Create your SaddleFit account, scan your horse, and line up a tree
              your maker can build on with confidence.
            </p>

            {/* Mobile Feature Cards (Stacked, above buttons) */}
            <div className="flex flex-col gap-3 md:hidden mt-8 mb-8 w-full">
              {features.map((feature, index) => (
                <FeatureItem
                  key={index}
                  Icon={feature.Icon}
                  text={feature.text}
                />
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-0 md:mt-[23px] w-full items-stretch sm:items-start">
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="border-[var(--color-primary)] border-2 text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white transition-colors order-2 sm:order-1 w-full sm:w-[160px] lg:w-[185px] font-montserrat font-semibold h-[44px] sm:h-[52px] py-2 sm:py-3 px-8 rounded-[50px] text-[14px] sm:text-[16px] leading-[28px] tracking-[-0.03em]"
                onClick={goLearnMore}
              >
                Learn more
              </Button>
              <Button
                type="button"
                size="lg"
                className="bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90 text-white transition-colors order-1 sm:order-2 w-full sm:w-[160px] lg:w-[185px] font-montserrat font-semibold h-[44px] sm:h-[52px] py-2 sm:py-3 px-8 rounded-[50px] text-[14px] sm:text-[16px] leading-[28px] tracking-[-0.03em]"
                onClick={goToSignup}
              >
                Sign up now
              </Button>
            </div>
          </div>

          {/* Desktop Feature Cards (Right side) */}
          <div className="hidden lg:flex flex-shrink-0 w-[356px] flex-col space-y-3">
            {features.map((feature, index) => (
              <FeatureItem
                key={index}
                Icon={feature.Icon}
                text={feature.text}
              />
            ))}
          </div>
        </div>

        {/* Tablet Feature Cards (Horizontal Row, below buttons) */}
        <div className="hidden md:flex lg:hidden flex-row gap-[10px] mt-12 items-start justify-start">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              Icon={feature.Icon}
              text={feature.text}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
